import { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, deleteDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { firebase, auth } from '../../../../services/firebase';
import { logError } from '../../../../utils/analytics';
import moment from 'moment';

export const useChatJourney = (toast) => {
  const [nodes, setNodes] = useState([]);
  const [isEnabled, setIsEnabled] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [lastSavedState, setLastSavedState] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [visibleHistory, setVisibleHistory] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Load chat journey and history from Firebase
  useEffect(() => {
    const loadChatJourneyAndHistory = async () => {
      try {
        setIsLoading(true);
        const user = auth.currentUser;
        if (!user) {
          setIsLoading(false);
          return;
        }

        // Load default journey
        const defaultRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'default');
        const defaultDoc = await getDoc(defaultRef);

        if (defaultDoc.exists()) {
          const journeyData = defaultDoc.data();
          if (journeyData.nodes && journeyData.nodes.length > 0) {
            setNodes(journeyData.nodes);
            setIsEnabled(journeyData.isEnabled || false);
            setLastSavedState({
              nodes: journeyData.nodes,
              isEnabled: journeyData.isEnabled || false
            });
          } else {
            setIsEnabled(journeyData.isEnabled || false);
          }
        }

        // Load versions and drafts
        const versionsRef = collection(firebase, 'users', user.uid, 'chatJourneys', 'versions', 'all');
        const versionsQuery = query(versionsRef, orderBy('updatedAt', 'desc'));
        const versionsSnapshot = await getDocs(versionsQuery);

        const publishedVersions = versionsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          type: 'published'
        }));

        // Load drafts
        const draftsRef = collection(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all');
        const draftsQuery = query(draftsRef, orderBy('updatedAt', 'desc'));
        const draftsSnapshot = await getDocs(draftsQuery);

        const drafts = draftsSnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          type: 'draft'
        }));

        // Merge and deduplicate by id (latest wins)
        const merged = [...publishedVersions, ...drafts];
        const dedupedMap = new Map();
        merged.forEach(item => {
          // Keep the most recent item per id
          const existing = dedupedMap.get(item.id);
          if (!existing || new Date(item.updatedAt) > new Date(existing.updatedAt)) {
            dedupedMap.set(item.id, item);
          }
        });
        const allVersions = Array.from(dedupedMap.values()).sort((a, b) =>
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );

        setEditHistory(allVersions);
        setTotalRecords(allVersions.length);

        if (allVersions.length > 0) {
          setSelectedVersion(allVersions[0]);
        }
      } catch (error) {
        logError('useChatJourney: load chat journey and history', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load chat journey and history'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadChatJourneyAndHistory();
  }, [toast]);

  // Track changes
  useEffect(() => {
    if (lastSavedState) {
      const nodesChanged = JSON.stringify(nodes) !== JSON.stringify(lastSavedState.nodes);
      const enabledChanged = isEnabled !== lastSavedState.isEnabled;
      setHasChanges(nodesChanged || enabledChanged);
    } else {
      setHasChanges(nodes.length > 0);
    }
  }, [nodes, isEnabled, lastSavedState]);

  // Add new useEffect for lazy loading
  useEffect(() => {
    const loadLazyData = () => {
      // Sort all history items by updatedAt in descending order (newest first)
      const sortedHistory = [...editHistory].sort((a, b) =>
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      setVisibleHistory(sortedHistory);
    };

    loadLazyData();
  }, [editHistory]);



  const saveChatJourney = async (isDraft = false, enabledState = isEnabled) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'You must be logged in to save chat journeys'
        });
        return;
      }

      const journeyData = {
        nodes,
        isEnabled: enabledState,
        isDraft,
        updatedAt: moment().unix(),
        version: editHistory.filter(v => v.type === 'published').length + 1
      };

      let journeyRef;
      if (isDraft) {
        journeyRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all', 'current_draft');
      } else {
        journeyRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'default');
        const versionRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'versions', 'all', 'v' + journeyData.version.toString());
        await setDoc(versionRef, journeyData);

        const draftRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all', 'current_draft');
        try {
          await deleteDoc(draftRef);
        } catch (error) {
          const { logEvent } = await import('../../../../utils/analytics');
          logEvent('CHAT_JOURNEY_NO_DRAFT', { userId: user?.uid });
        }
      }

      await setDoc(journeyRef, journeyData);

      const newHistoryItem = {
        ...journeyData,
        id: isDraft ? 'current_draft' : 'v' + journeyData.version.toString(),
        type: isDraft ? 'draft' : 'published'
      };

      setEditHistory(prev => {
        // Remove any existing item with same id and remove drafts
        const filtered = prev.filter(item => item.type !== 'draft' && item.id !== newHistoryItem.id);
        return [newHistoryItem, ...filtered];
      });

      setLastSavedState({
        nodes: JSON.parse(JSON.stringify(nodes)),
        isEnabled: enabledState
      });

      if (nodes.length === 0) {
        setShowInstructions(true);
      } else {
        setShowInstructions(false);
      }

      if (!isDraft) {
        setSelectedVersion(newHistoryItem);
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Chat journey ${isDraft ? 'drafted' : 'published'} successfully`
      });
    } catch (error) {
      logError('useChatJourney: save chat journey', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save chat journey'
      });
    }
  };

  const loadVersion = async (version) => {
    try {
      if (!version) {
        // If no version is provided, load the published version
        const publishedVersion = editHistory.find(item => item.type === 'published' && item.isEnabled);
        if (publishedVersion) {
          await loadVersion(publishedVersion);
        } else {
          setNodes([]);
          setSelectedVersion(null);
        }
        return;
      }

      if (!version.isEnabled) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Disabled Version',
          detail: 'This version is disabled and cannot be loaded'
        });
        return;
      }

      const user = auth.currentUser;
      if (!user) return;

      let journeyRef;
      if (version.isDraft) {
        journeyRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all', 'current_draft');
      } else {
        journeyRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'versions', 'all', 'v' + version.version.toString());
      }

      const journeyDoc = await getDoc(journeyRef);

      if (journeyDoc.exists()) {
        const journeyData = journeyDoc.data();

        // Only restore previous state if we're unselecting a draft
        if (version.isDraft && version.previousState && !version.isSelected) {
          setNodes(version.previousState.nodes);
          setIsEnabled(version.previousState.isEnabled);
          setHasChanges(version.previousState.hasChanges);
        } else {
          // Load the actual content of the version/draft
          setNodes(journeyData.nodes);
          setIsEnabled(journeyData.isEnabled);
        }

        setSelectedVersion(version);

        // Update last saved state when loading a version
        setLastSavedState({
          nodes: JSON.parse(JSON.stringify(journeyData.nodes)),
          isEnabled: journeyData.isEnabled
        });

        // Update instruction visibility based on data
        if (journeyData.nodes.length === 0) {
          setShowInstructions(true);
        } else {
          setShowInstructions(false);
        }
      }
    } catch (error) {
      logError('useChatJourney: load version', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load version'
      });
    }
  };

  const handleSaveDraft = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'You must be logged in to save drafts'
        });
        return;
      }

      // Get the current draft
      const currentDraft = editHistory.find(item => item.type === 'draft');
      if (!currentDraft) {
        toast.current?.show({
          severity: 'warn',
          summary: 'No Draft',
          detail: 'No draft to save'
        });
        return;
      }

      // Save as a new version (publish)
      const journeyData = {
        nodes: currentDraft.nodes,
        isEnabled: currentDraft.isEnabled,
        isDraft: false,
        updatedAt: moment().unix(),
        version: editHistory.filter(v => v.type === 'published').length + 1
      };

      // Save to default and versions
      const defaultRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'default');
      const versionRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'versions', 'all', 'v' + journeyData.version.toString());

      await setDoc(defaultRef, journeyData);
      await setDoc(versionRef, journeyData);

      // Delete the draft
      const draftRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all', 'current_draft');
      await deleteDoc(draftRef);

      // Update edit history
      const newHistoryItem = {
        ...journeyData,
        id: 'v' + journeyData.version.toString(),
        type: 'published'
      };

      setEditHistory(prev => {
        const filtered = prev.filter(item => item.type !== 'draft' && item.id !== newHistoryItem.id);
        return [newHistoryItem, ...filtered];
      });

      // Update last saved state
      setLastSavedState({
        nodes: JSON.parse(JSON.stringify(journeyData.nodes)),
        isEnabled: journeyData.isEnabled
      });

      // Update local state
      setNodes(journeyData.nodes);
      setIsEnabled(journeyData.isEnabled);
      setSelectedVersion(newHistoryItem);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Draft published successfully'
      });
    } catch (error) {
      logError('useChatJourney: save draft', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save draft'
      });
    }
  };

  const deleteDraft = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'You must be logged in to delete drafts'
        });
        return;
      }

      // Delete from Firebase
      await deleteDoc(doc(firebase, 'users', user.uid, 'chatJourneys', 'drafts', 'all', 'current_draft'));

      // Update local state
      setEditHistory(prev => prev.filter(item => !item.isDraft));

      // Reset tree to last published version
      const lastPublished = editHistory.find(item => !item.isDraft);
      if (lastPublished) {
        await loadVersion(lastPublished);
      } else {
        // If no published version, reset to default
        setNodes([{
          key: '0',
          data: {
            question: 'Hi there! How can I help you today?',
            response: 'Choose one of the options below.'
          },
          children: []
        }]);
        setIsEnabled(false);
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Draft Deleted',
        detail: 'The draft has been deleted',
        life: 3000,
      });
    } catch (error) {
      logError('useChatJourney: delete draft', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete draft',
        life: 3000,
      });
    }
  };

  const handleToggleChange = async (newEnabledState) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'You must be logged in to modify chat journeys'
        });
        return;
      }

      // Get the next version number from all published versions
      const nextVersion = editHistory
        .filter(v => v.type === 'published')
        .reduce((max, v) => Math.max(max, parseInt(v.version) || 0), 0) + 1;

      // Create a new version for the enable/disable change
      const journeyData = {
        nodes,
        isEnabled: newEnabledState,
        isDraft: false,
        updatedAt: moment().unix(),
        version: nextVersion,
        changeType: newEnabledState ? 'enabled' : 'disabled'
      };

      // Save to default and versions
      const defaultRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'default');
      const versionRef = doc(firebase, 'users', user.uid, 'chatJourneys', 'versions', 'all', 'v' + journeyData.version.toString());

      await setDoc(defaultRef, journeyData);
      await setDoc(versionRef, journeyData);

      // Update edit history
      const newHistoryItem = {
        ...journeyData,
        id: 'v' + journeyData.version.toString(),
        type: 'published'
      };

      // Add the new version to history, deduping by id
      setEditHistory(prev => {
        const filtered = prev.filter(item => item.id !== newHistoryItem.id);
        return [newHistoryItem, ...filtered];
      });

      // Update local state
      setIsEnabled(newEnabledState);
      setSelectedVersion(newHistoryItem);
      setLastSavedState({
        nodes: JSON.parse(JSON.stringify(nodes)),
        isEnabled: newEnabledState
      });

      // Show success message
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Chat journey ${newEnabledState ? 'enabled' : 'disabled'} successfully`
      });

      // Show instructions if enabled and no data exists
      if (newEnabledState && nodes.length === 0) {
        setShowInstructions(true);
      } else {
        setShowInstructions(false);
      }
    } catch (error) {
      logError('useChatJourney: update journey state', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update journey state'
      });
    }
  };

  return {
    nodes,
    setNodes,
    isEnabled,
    setIsEnabled,
    editHistory,
    selectedVersion,
    setSelectedVersion,
    hasChanges,
    showInstructions,
    setShowInstructions,
    saveChatJourney,
    loadVersion,
    visibleHistory,
    totalRecords,
    handleSaveDraft,
    deleteDraft,
    handleToggleChange,
    isLoading
  };
}; 